// src/hooks/useAnalytics.ts
// Hook que inicializa todos los listeners automáticos de analytics.
// Se usa UNA vez en el componente raíz (App.tsx).

import { useEffect, useRef } from 'react';
import {
  trackCampaignLanding,
  trackSectionView,
  trackScrollDepth,
  trackEngagementTime,
  trackJsError,
} from '@/lib/tracking';
import { initAttribution } from '@/lib/attribution';

export const useAnalytics = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // 0. First-touch attribution: persiste UTMs en sessionStorage.
    initAttribution();

    // 1. UTMs al cargar
    trackCampaignLanding();

    // 2. Scroll depth (25/50/75/100)
    const scrollMilestones = new Set<number>();
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const percent = Math.round((scrollTop / docHeight) * 100);
      [25, 50, 75, 100].forEach((m) => {
        if (percent >= m && !scrollMilestones.has(m)) {
          scrollMilestones.add(m);
          trackScrollDepth(m);
        }
      });
    };
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 200);
    };
    window.addEventListener('scroll', throttledScroll, { passive: true });

    // 3. Engagement timer (cada 30s)
    let seconds = 0;
    const timerInterval = setInterval(() => {
      seconds += 30;
      trackEngagementTime(seconds);
    }, 30000);

    // 4. Section visibility con IntersectionObserver
    const viewedSections = new Set<string>();
    let observer: IntersectionObserver | null = null;
    const observeSections = () => {
      const sections = document.querySelectorAll('[data-section]');
      if (sections.length === 0) {
        setTimeout(observeSections, 1000);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const name = (entry.target as HTMLElement).dataset.section;
              if (name && !viewedSections.has(name)) {
                viewedSections.add(name);
                trackSectionView(name);
              }
            }
          });
        },
        { threshold: 0.5 }
      );
      sections.forEach((s) => observer!.observe(s));
    };
    setTimeout(observeSections, 500);

    // 5. JS errors (window.onerror + unhandled promise rejections)
    const handleError = (event: ErrorEvent) => {
      const msg = event?.message || (event?.error && String(event.error.message)) || '';
      const src = event?.filename || (event?.error && event.error.stack?.split('\n')[1]) || 'unknown';
      trackJsError(msg, src);
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason: any = event?.reason;
      const msg =
        (reason && (reason.message || reason.toString?.())) ||
        'unhandled_rejection';
      const src = (reason && reason.stack && reason.stack.split('\n')[1]) || 'promise';
      trackJsError(`[promise] ${msg}`, src);
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      clearInterval(timerInterval);
      observer?.disconnect();
    };
  }, []);
};
